import type { ExportLogEntry, Page } from '@fruuit/shared'
import { Badge, Card, type Column, DataTable } from '@fruuit/ui'
import { useApiQuery } from '../../api/hooks'
import { shortDate } from '../calendar/time'

export const exportLogKey = (view: string) => ['exports', 'log', view] as const

const FORMAT_LABEL: Record<ExportLogEntry['format'], string> = {
  csv: 'CSV',
  html: 'Print report',
  json: 'Record',
}

/** Report names from the catalogue; the seeded history uses the `_csv`/`_report` variants. */
const REPORT_LABEL: Record<string, string> = {
  committee: 'Committee report',
  coverage: 'Coverage register',
  engagements: 'Engagement cases',
  updates: 'Published updates',
  published_updates: 'Published updates',
  record_download: 'Single record',
}

export function reportLabel(kind: string): string {
  if (kind.startsWith('records.')) return `Records: ${kind.slice(8).replace(/_/g, ' ')}`
  const base = kind.replace(/_(csv|report)$/, '')
  const label = REPORT_LABEL[base] ?? base.replace(/_/g, ' ')
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** Export history (P-RPT-003): every export with who, when, rows and the file hash. */
export function ExportHistory({ view }: { view: 'investor' | 'staff' }) {
  const query = useApiQuery<Page<ExportLogEntry>>(exportLogKey(view), '/exports/log', {
    query: { view, limit: 25 },
  })
  const columns: Column<ExportLogEntry>[] = [
    {
      id: 'when',
      header: 'Exported',
      cell: (e) => (
        <span className="whitespace-nowrap">
          {shortDate(e.created_at)}{' '}
          <span className="text-sage">{e.created_at.slice(11, 16)} UTC</span>
        </span>
      ),
    },
    {
      id: 'report',
      header: 'Report',
      cell: (e) => (
        <span>
          {reportLabel(e.kind)}
          {view === 'staff' && e.org ? (
            <span className="block text-xs text-sage">{e.org.name}</span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'format',
      header: 'Format',
      cell: (e) => (
        <Badge tone="outline" className="whitespace-nowrap">
          {FORMAT_LABEL[e.format]}
        </Badge>
      ),
    },
    {
      id: 'rows',
      header: 'Rows',
      align: 'right',
      cell: (e) => e.row_count.toLocaleString('en-GB'),
    },
    { id: 'by', header: 'By', cell: (e) => e.actor?.name ?? '—' },
    {
      id: 'hash',
      header: 'SHA-256',
      cell: (e) => (
        <code className="text-xs text-sage" title={e.sha256}>
          {e.sha256.slice(0, 12)}…
        </code>
      ),
    },
  ]
  return (
    <Card title="Export history" eyebrow="Logged with scope, rows and file hash">
      <DataTable
        caption="Export history"
        columns={columns}
        rows={query.data?.items ?? []}
        getRowId={(e) => e.id}
        loading={query.isLoading}
        empty={<p className="py-6 text-center text-sm text-sage">No exports yet.</p>}
      />
    </Card>
  )
}
